import { motion } from "framer-motion";
import { Cpu, Zap, Mountain, Building2, Sprout } from "lucide-react";
import { PHILOSOPHY, FOCUS } from "@/constants/testIds";

const PILLARS = [
  {
    slug: "technology",
    icon: Cpu,
    title: "Technology",
    body: "Capital into AI, modular blockchain infrastructure, privacy compute and the platforms shaping the next decade.",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=85&w=2000&auto=format&fit=crop",
  },
  {
    slug: "energy",
    icon: Zap,
    title: "Energy",
    body: "Strategic positions across energy infrastructure and the transition to lower-carbon systems.",
    img: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?q=85&w=2000&auto=format&fit=crop",
  },
  {
    slug: "resources",
    icon: Mountain,
    title: "Resources",
    body: "Long-duration exposure to the raw materials, minerals and natural resources underwriting global growth.",
    img: "https://images.unsplash.com/photo-1610375461246-83df859d849d?q=85&w=2000&auto=format&fit=crop",
  },
  {
    slug: "infrastructure",
    icon: Building2,
    title: "Infrastructure",
    body: "The physical and digital backbones — connectivity, transport and real assets that compound over decades.",
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=2000&auto=format&fit=crop",
  },
  {
    slug: "agritech",
    icon: Sprout,
    title: "Agritech",
    body: "Backing technology that makes food, water and land systems more resilient, productive and sustainable.",
    img: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?q=85&w=2000&auto=format&fit=crop",
  },
];

export const Focus = () => {
  return (
    <section
      id="focus"
      data-testid={FOCUS.root}
      className="relative py-16 lg:py-24 border-t border-[#E0DDD6]"
    >
      {/* Section-local glow */}
      <div
        aria-hidden
        className="absolute -top-32 right-[-15%] w-[60vw] h-[60vw] max-w-[900px] max-h-[900px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at center, rgba(19,98,7,0.08) 0%, rgba(19,98,7,0) 60%)",
          filter: "blur(50px)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="inline-block w-8 h-px bg-[#136207]" />
          <span className="overline text-[#136207]">02 · Focus</span>
        </motion.div>

        {/* PART 1 — INVESTMENT PHILOSOPHY */}
        <div data-testid={PHILOSOPHY.root} className="relative">
          <div
            aria-hidden
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[60vw] h-[60vw] max-w-[800px] max-h-[800px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(19,98,7,0.10) 0%, rgba(19,98,7,0) 55%)",
              filter: "blur(50px)",
            }}
          />
          <div className="relative max-w-5xl mx-auto text-center pt-6 pb-12 lg:pb-16">
            <motion.span
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="overline text-[#136207]"
            >
              Investment Philosophy
            </motion.span>

            <motion.blockquote
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-display text-[#1A1A1A] text-3xl sm:text-4xl lg:text-[52px] leading-[1.22] tracking-tight font-medium"
            >
              We Invest In{" "}
              <span className="text-[#136207]">Scalable Businesses</span>{" "}
              With{" "}
              <span className="text-[#136207]">Long-Term Impact</span>,{" "}
              <span className="text-[#136207]">Strong Fundamentals</span>{" "}
              And{" "}
              <span className="text-[#136207]">Operators</span>{" "}
              We Believe In — Across{" "}
              <span className="text-[#136207]">Cycles</span>,{" "}
              <span className="text-[#136207]">Geographies</span>{" "}
              And{" "}
              <span className="text-[#136207]">Generations</span>.
            </motion.blockquote>
          </div>
        </div>

        {/* PART 2 — INVESTMENT THESIS · FIVE PILLARS */}
        <div className="border-t border-[#E0DDD6] pt-12 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="inline-block w-8 h-px bg-[#136207]" />
            <span className="overline text-[#136207]">Investment Thesis</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="font-display text-[#1A1A1A] text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.02] font-medium max-w-4xl"
          >
            Five Pillars. <span className="text-[#136207]">One Thesis.</span>
          </motion.h2>
          <motion.h3
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.12 }}
            className="font-display text-3xl sm:text-4xl lg:text-[56px] tracking-tight leading-[1.05] font-medium mt-4"
          >
            <span className="italic font-light text-[#1A1A1A]">Invest for the</span>{" "}
            <span className="text-[#136207]">Future.</span>
          </motion.h3>

          {/* 5-pillar grid — 5 cols on desktop, balanced fallback on smaller */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.slug}
                  data-testid={FOCUS.card(p.slug)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="group relative bg-[#FFFFFF]/70 backdrop-blur-sm border border-[#136207]/30 hover:border-[#136207] transition-colors overflow-hidden min-h-[320px]"
                >
                  {/* Image — full crispness, subtle zoom on hover */}
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-[1.04]"
                    style={{ backgroundImage: `url(${p.img})` }}
                  />
                  {/* Bottom text-legibility gradient (only where copy sits) */}
                  <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-[#F7F6F3] via-[#F7F6F3]/90 to-transparent" />
                  {/* Small top nib so overline + icon read cleanly */}
                  <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#F7F6F3]/70 to-transparent" />
                  <span
                    aria-hidden
                    className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        "radial-gradient(circle at center, rgba(19,98,7,0.30) 0%, rgba(19,98,7,0) 70%)",
                      filter: "blur(24px)",
                    }}
                  />

                  <div className="relative h-full p-6 lg:p-7 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <span className="overline">0{i + 1}</span>
                      <div
                        className="w-9 h-9 border border-[#136207]/40 bg-[#FFFFFF]/70 backdrop-blur flex items-center justify-center"
                        style={{ boxShadow: "inset 0 0 18px rgba(19,98,7,0.18)" }}
                      >
                        <Icon className="w-4 h-4 text-[#136207]" strokeWidth={1.6} />
                      </div>
                    </div>
                    <div className="mt-10">
                      <h3 className="font-display text-[#1A1A1A] text-[22px] lg:text-[24px] tracking-tight leading-tight break-words">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-[#5C5A55] text-[13px] lg:text-sm leading-relaxed font-light">
                        {p.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Focus;
